import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static values = {
    data: Object,
    height: { type: String, default: '300px' }
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    // Clear any existing content
    this.element.innerHTML = ''

    // Create canvas element
    const canvas = document.createElement('canvas')
    this.element.appendChild(canvas)

    const data = this.dataValue
    const labels = data.labels || []
    const datasets = data.datasets || []

    // Build Chart.js datasets
    const chartDatasets = []

    // Find bar and line datasets
    const barDatasets = datasets.filter(d => d.type === 'bar')
    const lineDatasets = datasets.filter(d => d.type === 'line')

    // Bar colors
    const barColors = ['rgba(16, 185, 129, 0.7)', 'rgba(59, 130, 246, 0.7)']
    const barBorderColors = ['rgb(16, 185, 129)', 'rgb(59, 130, 246)']

    barDatasets.forEach((ds, index) => {
      chartDatasets.push({
        label: ds.name,
        data: ds.data,
        type: 'bar',
        backgroundColor: barColors[index] || barColors[0],
        borderColor: barBorderColors[index] || barBorderColors[0],
        borderWidth: 1,
        yAxisID: 'y',
        order: 2
      })
    })

    // Line dataset (performance %) - uses right Y axis
    lineDatasets.forEach((ds) => {
      chartDatasets.push({
        label: ds.name,
        data: ds.data,
        type: 'line',
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: 'rgb(239, 68, 68)',
        yAxisID: 'y1',
        order: 1,
        spanGaps: true
      })
    })

    // Create the chart
    this.chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: chartDatasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 15
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || ''
                const value = context.parsed.y
                if (context.dataset.yAxisID === 'y1') {
                  return `${label}: ${value?.toFixed(2) ?? 'N/A'}%`
                }
                return `${label}: R${value?.toLocaleString() ?? 0}`
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            }
          },
          y: {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'Earnings (ZAR)',
              color: '#6b7280'
            },
            ticks: {
              callback: function(value) {
                return 'R' + value.toLocaleString()
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          y1: {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: 'Return %',
              color: 'rgb(239, 68, 68)'
            },
            ticks: {
              callback: function(value) {
                return value.toFixed(1) + '%'
              },
              color: 'rgb(239, 68, 68)'
            },
            grid: {
              drawOnChartArea: false
            },
            min: 0
          }
        }
      }
    })
  }
}
